import { useState, FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { ErrorBanner } from "../components/ErrorBanner";
import { FilesField } from "../components/FilesField";
import { createPost, PostFileIn } from "../api/posts";
import { PostType, ExternalUrlKind } from "../types/gordost";

const POST_TYPES: Array<{ value: PostType; label: string; icon: string }> = [
  { value: "diploma", label: "Диплом", icon: "🏆" },
  { value: "project", label: "Проект", icon: "🎯" },
  { value: "initiative", label: "Инициатива", icon: "✨" },
  { value: "gratitude", label: "Благодарность", icon: "💝" },
  { value: "sport", label: "Спорт", icon: "⚽" },
];

function detectUrlKind(url: string): ExternalUrlKind {
  if (url.includes("rutube.ru")) return "rutube";
  if (url.includes("vk.com") || url.includes("vkvideo.ru")) return "vk";
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  return "other";
}

export function PostNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const studentId = parseInt(params.get("student_id") || "0", 10);

  const [postType, setPostType] = useState<PostType>("diploma");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [eventDate, setEventDate] = useState(
    new Date().toISOString().substring(0, 10)
  );
  const [externalUrl, setExternalUrl] = useState("");
  const [files, setFiles] = useState<PostFileIn[]>([]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState<{ id: number; title: string } | null>(
    null
  );

  function resetForm() {
    setSubmitted(null);
    setPostType("diploma");
    setTitle("");
    setBody("");
    setEventDate(new Date().toISOString().substring(0, 10));
    setExternalUrl("");
    setFiles([]);
    setError("");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!title.trim()) {
      setError("Укажите заголовок — что за достижение");
      return;
    }
    setLoading(true);
    try {
      const externalUrlKind = externalUrl ? detectUrlKind(externalUrl) : undefined;
      const created = await createPost({
        student_id: studentId,
        post_type: postType,
        title: title.trim(),
        body: body.trim(),
        event_date: eventDate || null,
        external_url: externalUrl || undefined,
        external_url_kind: externalUrlKind,
        files,
      });
      setSubmitted({ id: created.id, title: created.title });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Ошибка создания публикации");
    } finally {
      setLoading(false);
    }
  }

  if (!studentId) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-2xl mx-auto px-6 py-8">
          <p className="text-center text-[var(--color-muted)]">
            Сначала выберите ребёнка в кабинете.{" "}
            <Link to="/cabinet" className="text-[var(--color-accent)] underline">
              ← Назад
            </Link>
          </p>
        </main>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-2xl mx-auto px-6 py-8">
          <div className="bg-white border border-[var(--color-line)] p-8">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3 text-[var(--color-accent)]">✓</div>
              <h1 className="text-2xl mb-2">Отправлено на проверку</h1>
              <p className="text-sm text-[var(--color-muted)]">
                «{submitted.title}» ушло классному руководителю. Как только
                одобрят — публикация появится в летописи и станет видна
                директору.
              </p>
            </div>
            <VisibilityChain />
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Button
                variant="primary"
                onClick={() => navigate(`/cabinet/post/${submitted.id}`)}
              >
                Открыть публикацию
              </Button>
              <Button variant="ghost" onClick={() => navigate("/cabinet")}>
                В кабинет
              </Button>
              <button
                type="button"
                onClick={resetForm}
                className="text-sm text-[var(--color-accent)] underline ml-auto"
              >
                + Добавить ещё одно
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link
          to="/cabinet"
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] mb-2 inline-block"
        >
          ← В кабинет
        </Link>
        <h1 className="text-3xl mb-2">Опубликовать достижение</h1>
        <p className="text-sm text-[var(--color-muted)] mb-6">
          Публикация уйдёт на проверку классному руководителю.
        </p>
        <ErrorBanner message={error} />

        <form
          onSubmit={onSubmit}
          className="bg-white p-6 border border-[var(--color-line)]"
        >
          {/* Type */}
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium">Тип:</label>
            <div className="flex flex-wrap gap-2">
              {POST_TYPES.map((pt) => (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => setPostType(pt.value)}
                  className={`px-4 py-2 border-2 text-sm transition-colors ${
                    postType === pt.value
                      ? "bg-[var(--color-accent)] text-white border-[var(--color-accent)]"
                      : "bg-white border-[var(--color-line)] hover:border-[var(--color-accent)]"
                  }`}
                >
                  {pt.icon} {pt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Files — фото-первый порядок: главное действие родителя на телефоне */}
          <FilesField
            files={files}
            onChange={setFiles}
            onError={setError}
            onUploadingChange={setUploading}
          />

          <Input
            label="Заголовок"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            placeholder="Например: 1 место на районной олимпиаде по математике"
          />

          <Input
            label="Дата события"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            required
          />

          {/* Body — необязательное: для фото диплома достаточно заголовка */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              Описание{" "}
              <span className="text-[var(--color-muted)] font-normal">
                — необязательно
              </span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Можно добавить детали: название олимпиады, дисциплина, организатор. Если на фото и так всё видно — оставьте пустым."
              className="w-full px-4 py-3 border-2 border-[var(--color-line)] bg-white focus:outline-none focus:border-[var(--color-accent-2)]"
            />
            <p className="mt-1 text-xs text-[var(--color-muted)] text-right">
              {body.length} / 2000
            </p>
          </div>

          <Input
            label="Видео-ссылка (Rutube / VK / YouTube — опц.)"
            type="url"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            placeholder="https://rutube.ru/video/..."
            hint="Если у вас есть видео выступления, вставьте сюда ссылку."
          />

          <div className="mt-5">
            <VisibilityChain />
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/cabinet")}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading || uploading}
            >
              Отправить на проверку
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

/** Кто увидит публикацию — цепочка модерации. Снимает тревогу родителя
 *  (прямо отвечает на FAQ «Кто видит мои публикации?»). */
function VisibilityChain() {
  return (
    <div className="bg-[var(--color-paper)] border border-[var(--color-line)] p-4 text-sm">
      <div className="font-medium mb-2">Кто это увидит</div>
      <ul className="space-y-1 text-[var(--color-muted)] list-none p-0 m-0">
        <li>1 · Классный руководитель — проверит перед публикацией</li>
        <li>2 · Завуч — только если нужна подстраховка</li>
        <li>3 · Директор — чтобы пригласить лучших на приём</li>
        <li>4 · Вы и ваш ребёнок</li>
      </ul>
      <p className="mt-2 text-[var(--color-muted)]">
        Никаких общих лент, лайков и других родителей.
      </p>
    </div>
  );
}
